import React, { useCallback, useMemo, useRef, useState } from "react";
import { mergeStyles, useTheme } from "@fluentui/react";
import { Dropdown, DropdownProps, mergeClasses, Option, OptionProps, resolvePositioningShorthand } from "@fluentui/react-components";
import { usePcpDropdownOptionStyles, usePcpDropdownStyles, listboxOverlap } from "./baseComponents.styles";
import { ChevronDownFilled, ChevronUpFilled } from "@fluentui/react-icons";


export const PcpDropdown = (props: DropdownProps): JSX.Element => {
    const theme = useTheme();
    const styles = usePcpDropdownStyles();

    const rootRef = useRef<HTMLDivElement>(null);    // HTMLDivElement matches Slot<'div'>
    const listboxRef = useRef<HTMLDivElement>(null); // HTMLDivElement matches Slot<typeof Listbox>
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isOpenAbove, setIsOpenAbove] = useState(false);

    /** Handles positioning updates on scroll and resize events. Fluent UI's Dropdown internally enables Floating UI's autoUpdate, which subscribes to scroll and resize events on your behalf. 
     * On each scroll event, Floating UI repositions the listbox (updates its inline top/left style), then calls onPositioningEnd — which is your handlePositioningEnd. */
    const handlePositioningEnd = useCallback(() => {
      if (!listboxRef.current || !rootRef.current) { return; }
        // listboxRef and rootRef are captured from the outer closure...
        // BUT they are stable objects — same reference for the entire component lifetime
        // .current is READ AT CALL TIME, not captured as a value
        const above = listboxRef.current.getBoundingClientRect().bottom < rootRef.current.getBoundingClientRect().bottom;
        setIsOpenAbove(
            above
        );
    }, []); // [] is correct here

    const expandIcon = useMemo(
          () => isDropdownOpen ? <ChevronUpFilled /> : <ChevronDownFilled />,
          [isDropdownOpen]
      );

    return (
        <Dropdown
            {...props}
            appearance={'outline'}
            listbox={{
                className: mergeClasses(
                    styles.listbox,
                    isOpenAbove ? styles.listboxOpenAbove : styles.listboxOpenBelow
                ),
                ref: listboxRef
            }}
            root={{
                className: styles.root,
                ref: rootRef,
                style: isDropdownOpen ? { zIndex: 1000001 } : undefined,   //must exceed listbox z-index
            }}
            button={{ className: styles.button }}
            onOpenChange={(e, data) => {
                setIsDropdownOpen(data.open);
                props.onOpenChange?.(e, data);
            }}
            positioning={{
                ...resolvePositioningShorthand(props.positioning),  // converts any PositioningShorthand value (string or object) to a plain PositioningProps object, ready to spread
                offset: { crossAxis: 0, mainAxis: -listboxOverlap },
                onPositioningEnd: handlePositioningEnd,
            }}
            expandIcon={expandIcon}
        >
            {props.children}
        </Dropdown>
    )
};


export const PcpDropdownOption = (props: OptionProps): JSX.Element => {
    const styles = usePcpDropdownOptionStyles();

    return (
        <Option
            {...props}
            checkIcon={<></>}
            className={mergeStyles(styles.root)}
        />
    );
}
